import { useCallback, useEffect, useState } from 'react';
import { resolveApiErrorMessage } from '../utils/apiErrorMessages';
import { useTranslation } from 'react-i18next';
import { documentService } from '@services/document.service';
import { BLUEPRINT_EXTRACT_DPI, IMAGE_BLOB_CONCURRENCY } from '@constants/documentDetail';
import { useConfirm } from './useConfirm';

export interface DocumentImage {
  id: string;
  pageNumber: number;
  imageIndex: number;
  fileSize: number;
  width: number;
  height: number;
  aiDescription?: string | null;
  [key: string]: any;
}

export interface DocumentImagesState {
  images: DocumentImage[];
  imagesLoading: boolean;
  imageBlobUrls: Record<string, string>;
  failedImageIds: Set<string>;
  analyzingImages: boolean;
  analyzingImageId: string | null;
  reExtracting: boolean;
  forceReanalyze: boolean;
  setForceReanalyze: (value: boolean) => void;
  analyzeImage: (imageId: string) => Promise<void>;
  analyzeAllImages: () => Promise<void>;
  reExtractStandard: () => Promise<void>;
  reExtractBlueprint: () => Promise<void>;
}

/**
 * Owns the extracted-image list for one document: the list itself, the
 * authenticated blob URLs it renders through, and the AI/re-extraction actions.
 */
export function useDocumentImages(
  id: string | undefined,
  enabled: boolean,
): DocumentImagesState {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [images, setImages] = useState<DocumentImage[]>([]);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [imageBlobUrls, setImageBlobUrls] = useState<Record<string, string>>({});
  const [failedImageIds, setFailedImageIds] = useState<Set<string>>(new Set());
  const [analyzingImages, setAnalyzingImages] = useState(false);
  const [analyzingImageId, setAnalyzingImageId] = useState<string | null>(null);
  const [forceReanalyze, setForceReanalyze] = useState(false);
  const [reExtracting, setReExtracting] = useState(false);

  const loadImages = useCallback(async () => {
    if (!id) return;

    try {
      setImagesLoading(true);
      const imageData = await documentService.getDocumentImages(id);
      setImages((imageData.images || []) as DocumentImage[]);
    } catch (err) {
      console.error('Failed to load images:', err);
      setImages([]);
    } finally {
      setImagesLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id && enabled) {
      loadImages();
    }
  }, [id, enabled, loadImages]);

  useEffect(() => {
    return () => {
      Object.values(imageBlobUrls).forEach((url) => {
        URL.revokeObjectURL(url);
      });
    };
  }, [imageBlobUrls]);

  useEffect(() => {
    const loadImageBlobs = async () => {
      if (!id || images.length === 0) return;

      const results: Array<{ id: string; url: string; failed: boolean }> = [];
      for (let i = 0; i < images.length; i += IMAGE_BLOB_CONCURRENCY) {
        const batch = images.slice(i, i + IMAGE_BLOB_CONCURRENCY);
        const batchResults = await Promise.all(
          batch.map(async (image) => {
            try {
              const blobUrl = await documentService.getDocumentImageBlob(id, image.id);
              return { id: image.id, url: blobUrl, failed: false };
            } catch (err) {
              console.error(`Failed to load image blob for ${image.id}:`, err);
              return { id: image.id, url: '', failed: true };
            }
          }),
        );
        results.push(...batchResults);
      }

      const newBlobUrls: Record<string, string> = {};
      const newFailedIds = new Set<string>();

      results.forEach(({ id: imageId, url, failed }) => {
        if (url) {
          newBlobUrls[imageId] = url;
        }
        if (failed) {
          newFailedIds.add(imageId);
        }
      });

      // Revoking before replacing is what stops a leak on every re-extract.
      Object.values(imageBlobUrls).forEach((url) => {
        URL.revokeObjectURL(url);
      });

      setImageBlobUrls(newBlobUrls);
      setFailedImageIds(newFailedIds);
    };

    loadImageBlobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, images]);

  const analyzeImage = useCallback(
    async (imageId: string) => {
      if (!id) return;

      // The same handler backs the first analysis and the re-analyze button;
      // only the latter destroys an existing description.
      const overwrites = Boolean(images.find((img) => img.id === imageId)?.aiDescription);
      if (overwrites) {
        const confirmed = await confirm({
          title: t('documents.reanalyzeImageConfirmTitle'),
          description: t('documents.reanalyzeImageConfirmMessage'),
          confirmLabel: t('documents.reanalyzeImageConfirmAction'),
        });
        if (!confirmed) return;
      }

      try {
        setAnalyzingImageId(imageId);
        const result = await documentService.analyzeImage(id, imageId);

        setImages((prev) =>
          prev.map((img) =>
            img.id === imageId ? { ...img, aiDescription: result.aiDescription } : img,
          ),
        );

        alert(t('documents.imageAnalysisComplete'));
      } catch (error) {
        console.error('Failed to analyze image:', error);
        alert(resolveApiErrorMessage(t, error, { fallbackKey: 'documents.imageAnalysisFailed' }));
      } finally {
        setAnalyzingImageId(null);
      }
    },
    [id, images, confirm, t],
  );

  const analyzeAllImages = useCallback(async () => {
    if (!id) return;

    // Without the force flag the backend skips images that already have a
    // description, so the call is purely additive.
    if (forceReanalyze) {
      const confirmed = await confirm({
        title: t('documents.analyzeAllImagesConfirmTitle'),
        description: t('documents.analyzeAllImagesConfirmMessage'),
        confirmLabel: t('documents.analyzeAllImagesConfirmAction'),
      });
      if (!confirmed) return;
    }

    try {
      setAnalyzingImages(true);
      const result = await documentService.analyzeAllImages(id, forceReanalyze);

      result.results.forEach((r) => {
        if ((r.status === 'success' || r.status === 'skipped') && r.aiDescription) {
          setImages((prev) =>
            prev.map((img) =>
              img.id === r.imageId ? { ...img, aiDescription: r.aiDescription } : img,
            ),
          );
        }
      });

      alert(
        t(forceReanalyze ? 'documents.imagesReanalyzed' : 'documents.imagesAnalyzed', {
          count: result.analyzedImages,
        }),
      );
    } catch (error) {
      console.error('Failed to analyze images:', error);
      alert(resolveApiErrorMessage(t, error, { fallbackKey: 'documents.imageAnalysisFailed' }));
    } finally {
      setAnalyzingImages(false);
    }
  }, [id, forceReanalyze, confirm, t]);

  const reExtractStandard = useCallback(async () => {
    if (!id) return;

    const confirmed = await confirm({
      title: t('documents.reExtractImagesConfirmTitle'),
      description: t('documents.reExtractImagesConfirmMessage'),
      confirmLabel: t('documents.reExtractConfirmAction'),
    });
    if (!confirmed) return;

    try {
      setReExtracting(true);
      const result = await documentService.reExtractImages(id, {
        asBlueprint: false,
        runAiAnalysis: false,
      });

      alert(t('documents.imagesExtracted', { count: result.data.images_saved }));

      await loadImages();
    } catch (error) {
      console.error('Failed to re-extract images:', error);
      alert(t('documents.imageReExtractFailed'));
    } finally {
      setReExtracting(false);
    }
  }, [id, loadImages, confirm, t]);

  const reExtractBlueprint = useCallback(async () => {
    if (!id) return;

    const confirmed = await confirm({
      title: t('documents.reExtractBlueprintConfirmTitle'),
      description: t('documents.reExtractImagesConfirmMessage'),
      confirmLabel: t('documents.reExtractConfirmAction'),
    });
    if (!confirmed) return;

    try {
      setReExtracting(true);
      const result = await documentService.reExtractImages(id, {
        asBlueprint: true,
        dpi: BLUEPRINT_EXTRACT_DPI,
        runAiAnalysis: false,
      });

      alert(t('documents.blueprintImagesExtracted', { count: result.data.images_saved }));

      await loadImages();
    } catch (error) {
      console.error('Failed to re-extract blueprint images:', error);
      alert(t('documents.blueprintReExtractFailed'));
    } finally {
      setReExtracting(false);
    }
  }, [id, loadImages, confirm, t]);

  return {
    images,
    imagesLoading,
    imageBlobUrls,
    failedImageIds,
    analyzingImages,
    analyzingImageId,
    reExtracting,
    forceReanalyze,
    setForceReanalyze,
    analyzeImage,
    analyzeAllImages,
    reExtractStandard,
    reExtractBlueprint,
  };
}
